// frontend/src/components/blog/ArticleBody.js
//
// Renders a blog article's structured `blocks` array (from blogData.js) into the
// site's design language. No markdown library — the inline **bold** / [label](href)
// parser lives in components/shared/inlineMarkup.js (shared with the pricing
// cards, and the owner of the href allow-list); this module owns the BLOCK
// renderers only.
//
// Block types: h2 | h3 | p | ul | ol | table | quote | note
// All answers/paragraphs stay mounted in the DOM so the prerendered HTML carries
// the full text for crawlers.

import React from "react";
import { Box, Typography } from "@mui/material";
import { renderInline } from "../shared/inlineMarkup";

// Re-exported for this module's existing callers (pages/BlogArticle.js), which
// import it alongside the default ArticleBody export.
export { renderInline };

const HEAD_FONT = '"Heebo", sans-serif';
const BODY_FONT = '"DM Sans", sans-serif';
// Teal accent. Used ONLY for large text (h2, 22/26px bold) and non-text marks
// (list markers), so WCAG's 3:1 bar applies, not 4.5:1. #00A896 measured 2.98:1
// on white and missed it by 0.02; this is the smallest darkening that clears it
// (3.27:1) and is visually indistinguishable from the old brand teal.
// Small text (links, chips, meta) must use ACCENT_TEXT instead - see BlogArticle.js.
const ACCENT = "#00A08D";
const INK = "#2D3748";
const BODY = "#4B5563";

const bodySx = {
  fontFamily: BODY_FONT,
  fontSize: { xs: "15.5px", md: "17px" },
  lineHeight: 1.85,
  color: BODY,
  "& strong": { fontWeight: 700, color: "#374151" },
};

// Paragraph that honors "\n" as a line break.
function Para({ text, sx }) {
  const lines = text.split("\n");
  return (
    <Typography sx={{ my: { xs: 1.25, md: 1.5 }, ...bodySx, ...sx }}>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {renderInline(line)}
        </React.Fragment>
      ))}
    </Typography>
  );
}

const QUOTE_VARIANTS = {
  bad: { border: "#E57373", bg: "#FDF4F4" },
  good: { border: "#66BB6A", bg: "#F4FBF5" },
  plain: { border: "#7FEEE0", bg: "#F6F8FA" },
};

function BlogTable({ head, rows }) {
  const cellSx = {
    border: "1px solid #E2E8F0",
    p: { xs: "8px 10px", md: "10px 12px" },
    textAlign: "start",
    fontFamily: BODY_FONT,
    fontSize: { xs: "13.5px", md: "15px" },
  };
  return (
    <Box sx={{ overflowX: "auto", my: { xs: 2, md: 2.5 } }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 360 }}>
        <Box component="thead">
          <Box component="tr">
            {head.map((h, i) => (
              <Box
                component="th"
                key={i}
                sx={{ ...cellSx, background: "#EDFAF8", color: "#1F2D3D", fontWeight: 700 }}
              >
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, ri) => (
            <Box
              component="tr"
              key={ri}
              sx={{ "& td": ri % 2 === 1 ? { background: "#FAFDFD" } : {} }}
            >
              {row.map((cell, ci) => (
                <Box component="td" key={ci} sx={{ ...cellSx, color: BODY }}>
                  {renderInline(cell)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ListBlock({ ordered, items }) {
  return (
    <Box
      component={ordered ? "ol" : "ul"}
      sx={{
        my: { xs: 1.25, md: 1.5 },
        // Logical inset so markers sit inside the content in both RTL and LTR.
        marginInline: 0,
        paddingInlineStart: "1.5em",
        listStylePosition: "outside",
        "& li": {
          mb: { xs: 0.75, md: 1 },
          ...bodySx,
        },
        // marker color
        "& li::marker": { color: ACCENT },
      }}
    >
      {items.map((item, i) => (
        <Box component="li" key={i}>
          {renderInline(item)}
        </Box>
      ))}
    </Box>
  );
}

export default function ArticleBody({ blocks }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case "h2":
            return (
              <Typography
                key={i}
                component="h2"
                sx={{
                  fontFamily: HEAD_FONT,
                  fontSize: { xs: "22px", md: "26px" },
                  fontWeight: 800,
                  color: ACCENT,
                  lineHeight: 1.3,
                  mt: { xs: 4, md: 5 },
                  mb: { xs: 1.25, md: 1.5 },
                }}
              >
                {block.text}
              </Typography>
            );
          case "h3":
            return (
              <Typography
                key={i}
                component="h3"
                sx={{
                  fontFamily: HEAD_FONT,
                  fontSize: { xs: "18px", md: "20px" },
                  fontWeight: 700,
                  color: INK,
                  lineHeight: 1.35,
                  mt: { xs: 2.5, md: 3 },
                  mb: { xs: 0.75, md: 1 },
                }}
              >
                {block.text}
              </Typography>
            );
          case "p":
            return <Para key={i} text={block.text} />;
          case "ul":
            return <ListBlock key={i} items={block.items} />;
          case "ol":
            return <ListBlock key={i} ordered items={block.items} />;
          case "table":
            return <BlogTable key={i} head={block.head} rows={block.rows} />;
          case "note":
            return (
              <Para
                key={i}
                text={block.text}
                sx={{
                  fontStyle: "italic",
                  fontSize: { xs: "14px", md: "15px" },
                  color: "#6B7280",
                  my: { xs: 1.5, md: 1.75 },
                }}
              />
            );
          case "quote": {
            const v = QUOTE_VARIANTS[block.variant] || QUOTE_VARIANTS.plain;
            return (
              <Box
                key={i}
                sx={{
                  my: { xs: 1.5, md: 2 },
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: "10px",
                  background: v.bg,
                  borderInlineStart: `3px solid ${v.border}`,
                }}
              >
                <Typography sx={{ ...bodySx, color: "#44515F" }}>
                  {block.label && (
                    <strong style={{ color: "#374151" }}>{block.label} </strong>
                  )}
                  {renderInline(block.text)}
                </Typography>
              </Box>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}
